import UiModal from '../../../../js/components/UiModal/UiModal'
import { showAlchemerePopup } from '../../../../js/components/Alchemere'
import { Media } from '../../../../js/utils/const'

/* global landingInfo */

const TEMPLATE_CONFIG = {
  v1: {
    id: 'auto-payment-form',
    root: '.auto-payment-form',
  },
  v2: {
    id: 'auto-payment-form-v2',
    root: '.auto-payment-form-v2',
  },
}

const detectAutopaymentVersion = () => {
  if (document.querySelector(`#${TEMPLATE_CONFIG.v1.id}`)) {
    return 'v1'
  }

  if (document.querySelector(`#${TEMPLATE_CONFIG.v2.id}`)) {
    return 'v2'
  }

  return null
}

const getTemplateElement = (version) => version
  ? document.querySelector(`#${TEMPLATE_CONFIG[version].id}`)
  : null

export const hasAutoPaymentFormTemplate = () => Boolean(detectAutopaymentVersion())

const cloneAutoPaymentFormTemplate = (version) => {
  if (!version) {
    return null
  }

  const { root } = TEMPLATE_CONFIG[version]

  return getTemplateElement(version)
    ?.content.cloneNode(true)
    .querySelector(root) ?? null
}

const loadAutoPaymentFormClass = async (version) => {
  const importFn = version === 'v2'
    ? () => import('../AutoPaymentForm/v2/scripts')
    : () => import('../AutoPaymentForm/scripts')

  try {
    const module = await importFn()

    return module.AutoPaymentForm
  } catch (error) {
    window.Sentry?.captureException(error)
  }
}

const createAutoPaymentFormInstance = async (template, {
  nomenclatureId,
  methods,
  type,
  blockName,
  version
}) => {
  const AutoPaymentForm = await loadAutoPaymentFormClass(version)

  if (!AutoPaymentForm) {
    return null
  }

  const formInstance = new AutoPaymentForm({
    wrapper: document.createElement('div'),
    template,
    nomenclatureId,
    methods,
    type,
    blockName,
    mountToWrapper: false
  })

  formInstance.render()

  return formInstance
}

class AutopaymentModal extends UiModal {
  constructor(template, { nomenclatureId, blockName, methods, type = 'pay', callback, formInstance, email = null, formName = null }) {
    super(template, {
      blockTriggerName: blockName,
      isCenter: true,
      outerScroll: true,
      wrapperClass: 'autopayment-modal'
    })

    if (!methods) {
      const error = new Error('[Autopayment modal]: data is required')

      window.Sentry?.captureException(error)
      throw error
    }

    this.callback = callback
    this.methods = methods
    this.type = type
    this.nomenclatureId = nomenclatureId
    this.email = email
    this.formName = formName

    this.formInstance = formInstance
    this.formElement = template

    this.smMax = window.matchMedia(`(max-width: ${Media.SM_MAX}px)`)
  }

  beforeMounted() {
    if (!this.formInstance) {
      return
    }

    if (this.smMax.matches) {
      this.scrollingEl.addEventListener('scroll', this.modalScrollHandler)
      window.addEventListener('resize', this.calcFooterHeight)
    }

    this.smMax.addEventListener('change', this.smMediaChangeHandler)
    this.formInstance.form.addEventListener('form-success', this.formSuccessHandler)
  }

  mounted() {
    super.mounted()

    if (!this.formInstance) {
      return
    }

    if (this.smMax.matches) {
      this.setShadowForFooter(this.scrollingEl)
      this.calcFooterHeight()
    }

    this.formInstance.setDetailsHeight()
  }

  beforeUnmounted() {
    if (this.smMax.matches) {
      this.scrollingEl.removeEventListener('scroll', this.modalScrollHandler)
    }

    this.smMax.removeEventListener('change', this.smMediaChangeHandler)
    this.formInstance?.form?.removeEventListener('form-success', this.formSuccessHandler)
  }

  unmounted() {
    super.unmounted()
    this.formInstance?.destroy()
  }

  smMediaChangeHandler = (evt) => {
    if (!evt.matches) {
      this.scrollingEl.removeEventListener('scroll', this.modalScrollHandler)
      window.removeEventListener('resize', this.calcFooterHeight)
      return
    }

    this.scrollingEl.addEventListener('scroll', this.modalScrollHandler)
    window.addEventListener('resize', this.calcFooterHeight)
    this.setShadowForFooter(this.scrollingEl)
    this.calcFooterHeight()
  }

  setShadowForFooter(target) {
    if (!this.formInstance) {
      return
    }

    const isScroll = (target.scrollHeight - target.offsetHeight) !== target.scrollTop
    this.formInstance.template.classList.toggle('is-scroll', isScroll)
  }

  modalScrollHandler = (evt) => {
    this.setShadowForFooter(evt.target)
  }

  calcFooterHeight = () => {
    if (!this.smMax.matches) {
      this.formElement.style.paddingBottom = ''
      return
    }

    const footerHeight = this.formElement.querySelector('.auto-payment-form__footer')?.offsetHeight

    if (!footerHeight) {
      this.formElement.style.paddingBottom = ''
      return
    }

    this.formElement.style.paddingBottom = `${footerHeight - 40}px`
  }

  formSuccessHandler = () => {
    this.close()
  }

  beforeClose() {
    if (this.callback && typeof this.callback === 'function') {
      this.callback('lead')
      return
    }

    showAlchemerePopup({
      type: 'lead',
      blockName: this.blockTriggerName
    })
  }
}

export const showAutopaymentModal = async ({ methods, nomenclatureId, type, blockName, callback, email = null, formName = null } = {}) => {
  const version = detectAutopaymentVersion()
  const template = cloneAutoPaymentFormTemplate(version)

  if (!template) {
    return false
  }

  if (!methods) {
    try {
      const data = JSON.parse(template.dataset.payments)
      methods = data?.[nomenclatureId]
    } catch (error) {
      window.Sentry?.captureException(error)

      return false
    }
  }

  if (!Array.isArray(methods) || methods.length === 0) {
    return false
  }

  const formInstance = await createAutoPaymentFormInstance(template, {
    nomenclatureId,
    methods,
    type,
    blockName,
    version
  })

  if (!formInstance) {
    return false
  }

  new AutopaymentModal(template, {
    nomenclatureId,
    methods,
    type,
    blockName,
    callback,
    formInstance,
    email,
    formName
  }).init()

  window.$mindbox?.push('coursePaymentFormShow', {
    email,
    courseAMOID: landingInfo.landing_ID,
    formName,
    directionName: landingInfo.direction_name,
    directionId: landingInfo.direction_id
  })

  return true
}
